import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Collapse,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { ReactNode, useContext, useEffect, useState } from "react";
import { BentoCategory } from "schemas/dashboard";
import { LANGUAGES } from "schemas/languages";

import LocationAutocomplete from "components/LocationAutocomplete";
import {
  DiscoverSearchGeopoint,
  UserLocation,
} from "components/LocationAutocomplete/schema";

/* ── Following-size helpers ───────────────────────────────────────── */

const FOLLOWER_THRESHOLDS = [
  { value: 0, label: "No minimum" },
  { value: 10_000, label: "10K" },
  { value: 100_000, label: "100K" },
  { value: 500_000, label: "500K" },
  { value: 1_000_000, label: "1M" },
  { value: 10_000_000, label: "10M" },
];

const FOLLOWER_MAX_THRESHOLDS = [
  { value: 10_000, label: "10K" },
  { value: 100_000, label: "100K" },
  { value: 500_000, label: "500K" },
  { value: 1_000_000, label: "1M" },
  { value: 10_000_000, label: "10M" },
  { value: -1, label: "No maximum" },
];

/* ── Static content ───────────────────────────────────────────────── */

const TIPS = [
  "Enter Instagram usernames without the @ symbol",
  "Use advanced filters for more precise targeting",
];

/* ── Shared sx tokens ─────────────────────────────────────────────── */

const selectSx = {
  width: "100%",
  borderRadius: "10px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e5e7eb",
    borderWidth: 2,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
  },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#e5e7eb", borderWidth: 2 },
    "&:hover fieldset": { borderColor: "primary.main" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
  "& .MuiInputBase-input": {
    fontSize: 16,
    py: 1.5,
    px: 2,
    "&::placeholder": { color: "rgba(1,67,57,0.5)", opacity: 1 },
  },
};

const locationAutocompleteSx = {
  border: "2px solid #e5e7eb",
  borderRadius: "10px",
  height: 52,
  py: 1.5,
  px: 2,
  width: "100%",

  fontSize: 16,
  boxSizing: "border-box",
  "&:hover, &:focus": {
    outline: "none",
    borderColor: "primary.main",
  },
};

/* ── Sub-components ───────────────────────────────────────────────── */

function FilterSection({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
        <Typography
          sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{ color: "error.main", ml: 0.5, fontSize: 14 }}
            >
              *
            </Typography>
          )}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: 11, color: "grey.400", fontWeight: 500 }}>
            {hint}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

function AndDivider() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", my: 1 }}>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
      <Typography
        sx={{
          px: 1.5,
          fontSize: 11,
          fontWeight: 700,
          color: "grey.400",
          letterSpacing: 1,
        }}
      >
        AND
      </Typography>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
    </Box>
  );
}

function OrChipGroup({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
      {children}
    </Box>
  );
}

function CategorySelect({
  topLevelCategories,
  selectedCategoryIds,
  onToggle,
  onToggleAll,
}: {
  topLevelCategories: BentoCategory[];
  selectedCategoryIds: string[];
  onToggle: (subcategoryId: number, topLevelId: number) => void;
  onToggleAll: (category: BentoCategory) => void;
}) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box
      sx={{
        border: "2px solid #e5e7eb",
        borderRadius: "10px",
        maxHeight: 420,
        overflowY: "auto",
      }}
    >
      {topLevelCategories.map((category) => {
        const subcategoryIds = category.subcategories?.map((s) => s.id) || [];
        const isAllSelected = selectedCategoryIds.includes(
          `${category.id}:all`,
        );
        const selectedCount = isAllSelected
          ? subcategoryIds.length
          : subcategoryIds.filter((id) => selectedCategoryIds.includes(`${id}`))
              .length;
        const isExpanded = expandedIds.has(category.id);

        return (
          <Box key={category.id}>
            <ListItemButton
              onClick={() => toggleExpand(category.id)}
              sx={{ py: 1, px: 2 }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "text.primary",
                  flex: 1,
                }}
              >
                {category.name}
                {selectedCount > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 1,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "primary.main",
                      backgroundColor: "rgba(1,67,57,0.08)",
                      borderRadius: "10px",
                      px: 1,
                      py: 0.25,
                    }}
                  >
                    {isAllSelected ? "All" : selectedCount}
                  </Typography>
                )}
              </Typography>
              <Box
                component="i"
                className={
                  isExpanded
                    ? "fa-solid fa-chevron-up"
                    : "fa-solid fa-chevron-down"
                }
                sx={{ fontSize: 12, color: "grey.500" }}
              />
            </ListItemButton>
            <Collapse in={isExpanded}>
              <Box sx={{ pl: 1 }}>
                {category.subcategories?.map((sub) => (
                  <ListItemButton
                    key={sub.id}
                    onClick={() => onToggle(sub.id, category.id)}
                    sx={{ py: 0.25, px: 1 }}
                  >
                    <Checkbox
                      checked={
                        isAllSelected ||
                        selectedCategoryIds.includes(`${sub.id}`)
                      }
                      disabled={isAllSelected}
                      size="small"
                      sx={{ "&.Mui-checked": { color: "primary.main" } }}
                    />
                    <ListItemText
                      primary={sub.name}
                      primaryTypographyProps={{
                        fontSize: 13,
                        color: "text.primary",
                      }}
                    />
                  </ListItemButton>
                ))}
                <ListItemButton
                  onClick={() => onToggleAll(category)}
                  sx={{ py: 0.25, px: 1 }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    size="small"
                    sx={{ "&.Mui-checked": { color: "primary.main" } }}
                  />
                  <ListItemText
                    primary="All"
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  />
                </ListItemButton>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

/* ── Main component ───────────────────────────────────────────────── */

interface ConfigureInstagramTrackedCreatorsProps {
  draftId: number;
  onNext: (config: Record<string, any>) => Promise<void>;
  initialConfig?: Record<string, any> | null;
}

export default function ConfigureInstagramTrackedCreators({
  draftId: _draftId,
  onNext,
  initialConfig,
}: ConfigureInstagramTrackedCreatorsProps) {
  const { topLevelCategories, fetchBentoCategories } = useContext(
    BentoCategoriesContext,
  );
  const { discoverTab } = useContext(DiscoverViewContext);
  const sidebarOpen = discoverTab !== null;

  const [usernames, setUsernames] = useState<string[]>(
    initialConfig?.instagram_usernames ?? [],
  );
  const [usernameInput, setUsernameInput] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(
    initialConfig?.category_ids?.map(String) ?? [],
  );
  const [geopoints, setGeopoints] = useState<DiscoverSearchGeopoint[]>(
    initialConfig?.geopoints ?? [],
  );
  const [minFollowers, setMinFollowers] = useState<number | "">(
    initialConfig?.min_instagram_followers ?? "",
  );
  const [maxFollowers, setMaxFollowers] = useState<number | "">(
    initialConfig?.max_instagram_followers ?? "",
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
    initialConfig?.languages ?? [],
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (topLevelCategories.length === 0) {
      fetchBentoCategories();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [loading, setLoading] = useState(false);

  /* ── Username handlers ── */

  const addUsername = () => {
    const parts = usernameInput
      .split(",")
      .map((s) => s.trim().replace(/^@/, ""))
      .filter(Boolean);
    setUsernameError(null);

    if (parts.length === 0) {
      setUsernameInput("");
      return;
    }

    const valid: string[] = [];
    let hasInvalid = false;
    for (const part of parts) {
      if (!/^[a-zA-Z0-9._]{1,30}$/.test(part)) {
        hasInvalid = true;
        continue;
      }
      if (!usernames.includes(part) && !valid.includes(part)) {
        valid.push(part);
      }
    }

    if (valid.length > 0) {
      setUsernames((prev) => [...prev, ...valid]);
    }
    setUsernameInput("");
    if (hasInvalid) {
      setUsernameError(
        "Some usernames were skipped — usernames can only contain letters, numbers, periods, and underscores (max 30 characters).",
      );
    }
  };

  const removeUsername = (index: number) => {
    setUsernames((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── Category handlers ── */

  const toggleCategory = (subcategoryId: number, topLevelId: number) => {
    const key = `${subcategoryId}`;
    const allKey = `${topLevelId}:all`;

    setSelectedCategoryIds((prev) => {
      if (prev.includes(allKey)) {
        const category = topLevelCategories.find((c) => c.id === topLevelId);
        const siblingKeys = (category?.subcategories || [])
          .map((s) => `${s.id}`)
          .filter((k) => k !== key);
        return [...prev.filter((x) => x !== allKey), ...siblingKeys];
      }

      return prev.includes(key)
        ? prev.filter((x) => x !== key)
        : [...prev, key];
    });
  };

  const toggleAllForCategory = (category: BentoCategory) => {
    const allKey = `${category.id}:all`;
    const subcategoryKeys = (category.subcategories || []).map(
      (s) => `${s.id}`,
    );

    setSelectedCategoryIds((prev) => {
      if (prev.includes(allKey)) {
        return prev.filter((x) => x !== allKey);
      }
      return [...prev.filter((x) => !subcategoryKeys.includes(x)), allKey];
    });
  };

  /* ── Location handlers ── */

  const handleLocationChange = (userLocation: UserLocation) => {
    if (!userLocation?.latitude || !userLocation?.longitude) return;
    setLocationError(null);
    setFieldErrors((prev) => {
      const { locations: _, ...rest } = prev;
      return rest;
    });

    const addressComponents = userLocation.address_components;
    const city = addressComponents?.find(
      (x) =>
        x?.types?.includes("administrative_area_level_3") ||
        x?.types?.includes("locality"),
    )?.longText;
    const region = addressComponents?.find((x) =>
      x?.types?.includes("administrative_area_level_1"),
    )?.longText;

    if (city || region) {
      setLocationError(
        "Please select a country. Cities and regions are not supported.",
      );
      return;
    }

    const countryName = addressComponents?.find((x) =>
      x?.types?.includes("country"),
    )?.longText;
    const label = countryName || addressComponents?.[0]?.longText;

    const newGeopoint: DiscoverSearchGeopoint = {
      country_code: userLocation.country_code,
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
      is_country: true,
      label,
    };

    const isDuplicate = geopoints.some(
      (g) =>
        g.latitude === newGeopoint.latitude &&
        g.longitude === newGeopoint.longitude,
    );
    if (!isDuplicate) {
      setGeopoints((prev) => [...prev, newGeopoint]);
    }
  };

  const removeGeopoint = (index: number) => {
    setGeopoints((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    // Auto-submit pending username input (split by comma)
    let finalUsernames = [...usernames];
    const pendingRaw = usernameInput.trim();
    if (pendingRaw) {
      const parts = pendingRaw
        .split(",")
        .map((s) => s.trim().replace(/^@/, ""))
        .filter(Boolean);
      let hasInvalid = false;
      for (const part of parts) {
        if (!/^[a-zA-Z0-9._]{1,30}$/.test(part)) {
          hasInvalid = true;
          continue;
        }
        if (!finalUsernames.includes(part)) {
          finalUsernames = [...finalUsernames, part];
        }
      }
      if (hasInvalid) {
        setUsernameError(
          "Some usernames were skipped — usernames can only contain letters, numbers, periods, and underscores (max 30 characters).",
        );
      } else {
        setUsernameError(null);
      }
      setUsernames(finalUsernames);
      setUsernameInput("");
    }

    // Validate required fields
    const errors: Record<string, string> = {};
    if (finalUsernames.length === 0) {
      errors.usernames = "Please add at least one Instagram username.";
    }
    if (geopoints.length === 0) {
      errors.locations = "Please add at least one country.";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      const config: Record<string, any> = {
        instagram_usernames: finalUsernames,
      };
      if (geopoints.length > 0) {
        config.geopoints = geopoints;
      }
      if (selectedCategoryIds.length > 0) {
        const expandedIds: string[] = [];
        for (const key of selectedCategoryIds) {
          if (key.endsWith(":all")) {
            const parentId = parseInt(key.split(":")[0], 10);
            const parent = topLevelCategories.find((c) => c.id === parentId);
            if (parent?.subcategories) {
              expandedIds.push(
                ...parent.subcategories.map((sub) => `${sub.id}`),
              );
            }
          } else {
            expandedIds.push(key);
          }
        }
        config.category_ids = expandedIds;
      }
      if (minFollowers !== "" && minFollowers > 0) {
        config.min_instagram_followers = minFollowers;
      }
      if (maxFollowers !== "" && maxFollowers !== -1) {
        config.max_instagram_followers = maxFollowers;
      }
      if (selectedLanguages.length > 0) {
        config.languages = selectedLanguages;
      }
      await onNext(config);
    } catch {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 976, mx: "auto" }}>
      {/* Source header */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: "#fce8ec",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="span"
            className="fa-brands fa-instagram"
            sx={{ fontSize: 18, color: "#c03d5e" }}
          />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 24,
              color: "text.primary",
              lineHeight: "32px",
            }}
          >
            Instagram Tracked Creators
          </Typography>
          <Typography
            sx={{ fontSize: 16, color: "text.secondary", lineHeight: "24px" }}
          >
            Track specific Instagram creators and discover partnership
            opportunities
          </Typography>
        </Box>
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* ── Left panel: form card ── */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            p: 3,
          }}
        >
          {/* Instagram Usernames */}
          <FilterSection
            label="Instagram Usernames"
            required
            hint="press Enter to add"
          >
            <TextField
              fullWidth
              placeholder="Enter a username and press Enter"
              value={usernameInput}
              onChange={(e) => {
                setUsernameInput(e.target.value);
                setUsernameError(null);
                setFieldErrors((prev) => {
                  const { usernames: _, ...rest } = prev;
                  return rest;
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addUsername();
                }
              }}
              error={!!usernameError}
              sx={textFieldSx}
            />
            {usernameError && (
              <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                {usernameError}
              </Typography>
            )}
            {!usernameError && fieldErrors.usernames && (
              <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                {fieldErrors.usernames}
              </Typography>
            )}
            {usernames.length > 0 && (
              <OrChipGroup>
                {usernames.map((username, i) => (
                  <Chip
                    key={`username-${i}`}
                    label={`@${username}`}
                    size="small"
                    icon={
                      <Box
                        component="i"
                        className="fa-brands fa-instagram"
                        sx={{ fontSize: 11 }}
                      />
                    }
                    onDelete={() => removeUsername(i)}
                  />
                ))}
              </OrChipGroup>
            )}
            {usernames.length < 2 && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fee685",
                  borderRadius: "10px",
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-lightbulb"
                  sx={{
                    fontSize: 13,
                    color: "#b45309",
                    mt: "2px",
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 12, color: "#92400e" }}>
                  We suggest adding 3-4+ creators for best results — tracking
                  more creators gives this automation a wider pool of brands to
                  pitch.
                </Typography>
              </Box>
            )}
          </FilterSection>

          <AndDivider />

          {/* Location */}
          <FilterSection label="Country" required hint="matches any">
            <Box
              sx={{ mb: 1 }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <LocationAutocomplete
                handleLocationChange={handleLocationChange}
                customStyling={locationAutocompleteSx}
                placeholderText="Search for a country"
              />
            </Box>
            {locationError && (
              <Typography sx={{ fontSize: 12, color: "error.main", mb: 1 }}>
                {locationError}
              </Typography>
            )}
            {!locationError && fieldErrors.locations ? (
              <Typography sx={{ fontSize: 12, color: "error.main", mb: 1 }}>
                {fieldErrors.locations}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 12, color: "grey.500", mb: 1 }}>
                Add the countries where you want to find brands
              </Typography>
            )}

            {geopoints.length > 0 && (
              <OrChipGroup>
                {geopoints.map((g, i) => (
                  <Chip
                    key={`geo-${i}`}
                    label={g.label || "Location"}
                    size="small"
                    icon={
                      <Box
                        component="i"
                        className="fa-solid fa-location-dot"
                        sx={{ fontSize: 11 }}
                      />
                    }
                    onDelete={() => removeGeopoint(i)}
                  />
                ))}
              </OrChipGroup>
            )}
          </FilterSection>

          {/* Advanced Filters — collapsible card */}
          <Box
            sx={{
              border: "2px solid rgba(1,67,57,0.1)",
              borderRadius: "14px",
              overflow: "hidden",
              mt: 1,
            }}
          >
            {/* Header — clickable toggle */}
            <Box
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                height: 70,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "primary.main",
                    lineHeight: "20px",
                  }}
                >
                  Advanced Filters
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: "rgba(1,67,57,0.6)",
                    lineHeight: "16px",
                  }}
                >
                  Fine-tune with additional criteria
                </Typography>
              </Box>
              <Box
                component="i"
                className={
                  showAdvanced
                    ? "fa-solid fa-chevron-up"
                    : "fa-solid fa-chevron-down"
                }
                sx={{ fontSize: 14, color: "rgba(1,67,57,0.4)" }}
              />
            </Box>

            {/* Collapsible content */}
            <Collapse in={showAdvanced}>
              <Box
                sx={{
                  backgroundColor: "#fafaf6",
                  borderTop: "1px solid rgba(1,67,57,0.1)",
                  px: 2.5,
                  pt: 2.5,
                  pb: 1,
                }}
              >
                {/* Instagram Following Size */}
                <FilterSection label="Instagram Following Size">
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <Select<number | string>
                      displayEmpty
                      value={minFollowers}
                      onChange={(e) => {
                        const val = e.target.value;
                        setMinFollowers(val === "" ? "" : Number(val));
                      }}
                      input={<OutlinedInput />}
                      renderValue={(selected) =>
                        selected === "" ? (
                          <Typography
                            sx={{ color: "rgba(1,67,57,0.5)", fontSize: 14 }}
                          >
                            Min
                          </Typography>
                        ) : (
                          FOLLOWER_THRESHOLDS.find((t) => t.value === selected)
                            ?.label || `${selected}`
                        )
                      }
                      sx={{ ...selectSx, flex: 1 }}
                    >
                      {FOLLOWER_THRESHOLDS.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>

                    <Typography
                      sx={{ color: "grey.400", fontSize: 14, flexShrink: 0 }}
                    >
                      to
                    </Typography>

                    <Select<number | string>
                      displayEmpty
                      value={maxFollowers}
                      onChange={(e) => {
                        const val = e.target.value;
                        setMaxFollowers(val === "" ? "" : Number(val));
                      }}
                      input={<OutlinedInput />}
                      renderValue={(selected) =>
                        selected === "" ? (
                          <Typography
                            sx={{ color: "rgba(1,67,57,0.5)", fontSize: 14 }}
                          >
                            Max
                          </Typography>
                        ) : (
                          FOLLOWER_MAX_THRESHOLDS.find(
                            (t) => t.value === selected,
                          )?.label || `${selected}`
                        )
                      }
                      sx={{ ...selectSx, flex: 1 }}
                    >
                      {FOLLOWER_MAX_THRESHOLDS.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </FilterSection>

                {/* Categories */}
                <FilterSection label="Categories" hint="matches any">
                  <CategorySelect
                    topLevelCategories={topLevelCategories}
                    selectedCategoryIds={selectedCategoryIds}
                    onToggle={toggleCategory}
                    onToggleAll={toggleAllForCategory}
                  />
                  {selectedCategoryIds.length > 0 && (
                    <OrChipGroup>
                      {selectedCategoryIds.map((key) => {
                        let label: string;
                        let onDelete: () => void;

                        if (key.endsWith(":all")) {
                          const topId = parseInt(key.split(":")[0], 10);
                          const cat = topLevelCategories.find(
                            (c) => c.id === topId,
                          );
                          label = cat ? `${cat.name} (All)` : key;
                          onDelete = () =>
                            cat
                              ? toggleAllForCategory(cat)
                              : setSelectedCategoryIds((prev) =>
                                  prev.filter((x) => x !== key),
                                );
                        } else {
                          const subId = parseInt(key, 10);
                          const parent = topLevelCategories.find((c) =>
                            c.subcategories?.some((s) => s.id === subId),
                          );
                          const sub = parent?.subcategories?.find(
                            (s) => s.id === subId,
                          );
                          label = sub?.name || key;
                          onDelete = () =>
                            toggleCategory(subId, parent?.id ?? 0);
                        }

                        return (
                          <Chip
                            key={key}
                            label={label}
                            size="small"
                            onDelete={onDelete}
                          />
                        );
                      })}
                    </OrChipGroup>
                  )}
                </FilterSection>

                {/* Languages */}
                <FilterSection label="Languages" hint="matches any">
                  <Autocomplete
                    multiple
                    options={LANGUAGES}
                    getOptionLabel={(option) => option.name}
                    value={LANGUAGES.filter((l) =>
                      selectedLanguages.includes(l.key),
                    )}
                    onChange={(_, newValue) => {
                      setSelectedLanguages(newValue.map((v) => v.key));
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.key === value.key
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          selectedLanguages.length === 0
                            ? "All languages"
                            : undefined
                        }
                        sx={textFieldSx}
                      />
                    )}
                    ChipProps={{ size: "small" }}
                    sx={{ width: "100%" }}
                  />
                  <Typography sx={{ fontSize: 12, color: "grey.500", mt: 0.5 }}>
                    Only brands that communicate in these languages will be
                    included. Leave empty for all languages.
                  </Typography>
                </FilterSection>
              </Box>
            </Collapse>
          </Box>

          {/* Next button */}
          <LoadingButton
            variant="contained"
            fullWidth
            loading={loading}
            onClick={handleNext}
            sx={{
              mt: 3,
              borderRadius: "10px",
              backgroundColor: "primary.main",
              fontWeight: 600,
              fontSize: 16,
              py: 1.5,
              textTransform: "none",
            }}
          >
            Next
          </LoadingButton>
        </Box>

        {/* ── Right panel: tips & info ── */}
        <Box
          sx={{
            width: 310,
            flexShrink: 0,
            display: sidebarOpen ? "none" : { xs: "none", md: "flex" },
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Tips card */}
          <Box
            sx={{
              backgroundColor: "#fffbeb",
              border: "1px solid #fee685",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Box
              sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}
            >
              <Box
                component="span"
                className="fa-solid fa-lightbulb"
                sx={{ fontSize: 18, color: "#7b3306" }}
              />
              <Typography
                sx={{ fontWeight: 600, fontSize: 18, color: "#7b3306" }}
              >
                Tips
              </Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {TIPS.map((tip, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    color: "#e17100",
                    mb: 1,
                    "&::marker": { color: "#e17100" },
                  }}
                >
                  <Typography
                    sx={{ fontSize: 14, color: "#7b3306", lineHeight: "20px" }}
                  >
                    {tip}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* How filters work card */}
          <Box
            sx={{
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: 14, color: "#166534", mb: 1 }}
            >
              How filters work
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <Box component="li" sx={{ color: "#15803d", mb: 0.5 }}>
                <Typography
                  sx={{ fontSize: 13, color: "#15803d", lineHeight: "20px" }}
                >
                  Multiple options in one filter? Matches any of them (e.g. New
                  York or LA)
                </Typography>
              </Box>
              <Box component="li" sx={{ color: "#15803d" }}>
                <Typography
                  sx={{ fontSize: 13, color: "#15803d", lineHeight: "20px" }}
                >
                  Different filter types narrow results — brands must match all
                  of them
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
